import { ColumnDef } from '@tanstack/react-table';

/**
 * Definition for table action buttons
 */
export interface ActionDefinition<T> {
  id: string;
  label: string;
  icon: React.ReactNode;
  onClick: (item: T) => void;
  intent?: 'default' | 'primary' | 'secondary' | 'danger' | 'outline' | 'ghost' | 'link';
  disabled?: (item: T) => boolean;
  hidden?: (item: T) => boolean;
}

/**
 * Creates the configuration for a standardized action column
 * 
 * Note: The actual JSX implementation should be done in the component file.
 * This function provides the column definition structure.
 * 
 * @returns ColumnDef configuration for actions column
 */
export function getActionColumnConfig<T>(): Omit<ColumnDef<T>, 'cell'> {
  return {
    id: 'actions',
    header: 'Actions',
    enableSorting: false,
    enableResizing: false,
    size: 120, // Fixed width for action column
  };
}

/**
 * Default pagination thresholds for smart pagination strategy
 * These can be overridden per table based on data complexity and performance needs
 */
export const DEFAULT_PAGINATION_THRESHOLDS: PaginationThresholds = {
  noPagination: 15,      // ≤ 15 items: no pagination (ALL items visible)
  clientPagination: 200, // 16-200 items: client-side pagination
  serverPagination: 201  // > 200 items: server-side pagination
};

/**
 * Pagination threshold configuration for specific table types
 */
export interface PaginationThresholds {
  /** Maximum items to show without pagination (ALL must be visible) */
  noPagination: number;
  /** Maximum items for client-side pagination */
  clientPagination: number;
  /** Minimum items requiring server-side pagination */
  serverPagination?: number; // Optional - defaults to clientPagination + 1
}

/**
 * Pre-configured thresholds for common table types
 */
export const TABLE_PAGINATION_PRESETS = {
  /** Simple tables: settings, status lists, small reference data */
  SIMPLE: {
    noPagination: 25,      // Can show more items without performance impact
    clientPagination: 500, // Simple data handles large client-side datasets well
  },
  /** Standard tables: users, basic CRUD tables */
  STANDARD: {
    noPagination: 15,      // Default conservative approach
    clientPagination: 200, // Balanced performance
  },
  /** Complex tables: rich UI components, nested data, images */
  COMPLEX: {
    noPagination: 10,      // Fewer items due to rendering complexity
    clientPagination: 100, // Lower threshold due to performance impact
  },
  /** Heavy tables: large data objects, multiple API calls per row */
  HEAVY: {
    noPagination: 5,       // Very few items without pagination
    clientPagination: 50,  // Early server-side pagination
  },
} as const;

/**
 * Creates a smart pagination configuration that automatically chooses the best strategy
 * based on data size and performance characteristics
 * 
 * @param dataLength - Number of items in dataset
 * @param preset - Performance preset or custom thresholds
 * @returns Complete pagination configuration ready for DataTable
 */
export function createSmartPaginationConfig(
  dataLength: number,
  preset: keyof typeof TABLE_PAGINATION_PRESETS | PaginationThresholds = 'STANDARD'
) {
  const thresholds = typeof preset === 'string' ? TABLE_PAGINATION_PRESETS[preset] : preset;
  const strategy = getPaginationStrategy(dataLength, thresholds);
  const paginationConfig = getDataTablePaginationConfig(dataLength, thresholds, strategy);
  
  return {
    strategy,
    config: paginationConfig,
    thresholds,
    validation: validateNoPaginationConfig(dataLength, paginationConfig, thresholds),
  };
}

/**
 * Determines the optimal pagination strategy based on data size and configurable thresholds
 * 
 * @param dataLength - Number of items in dataset
 * @param thresholds - Custom thresholds or preset configuration
 * @returns Recommended pagination strategy
 */
export function getPaginationStrategy(
  dataLength: number, 
  thresholds: PaginationThresholds = DEFAULT_PAGINATION_THRESHOLDS
): 'none' | 'client' | 'server' {
  if (dataLength <= thresholds.noPagination) {
    return 'none';
  } else if (dataLength <= thresholds.clientPagination) {
    return 'client';
  } else {
    return 'server';
  }
}

/**
 * Gets the appropriate DataTable pagination configuration based on data size and strategy
 * 
 * @param dataLength - Number of items in dataset
 * @param thresholds - Custom thresholds or preset configuration
 * @param strategy - Optional override for pagination strategy
 * @returns Pagination configuration or false for no pagination
 */
export function getDataTablePaginationConfig(
  dataLength: number,
  thresholds: PaginationThresholds = DEFAULT_PAGINATION_THRESHOLDS,
  strategy?: 'none' | 'client' | 'server'
): typeof CLIENT_SIDE_PAGINATION_CONFIG | typeof SERVER_SIDE_PAGINATION_CONFIG | false {
  const actualStrategy = strategy || getPaginationStrategy(dataLength, thresholds);
  
  switch (actualStrategy) {
    case 'none':
      return false; // No pagination - ALL items must be visible
    case 'client':
      return CLIENT_SIDE_PAGINATION_CONFIG;
    case 'server':
      return SERVER_SIDE_PAGINATION_CONFIG;
    default:
      return false;
  }
}

/**
 * Validates that a no-pagination table configuration is correct
 * 
 * @param dataLength - Number of items in dataset
 * @param paginationConfig - The pagination configuration being used
 * @param thresholds - Custom thresholds or preset configuration
 * @returns Warning message if configuration is problematic
 */
export function validateNoPaginationConfig(
  dataLength: number,
  paginationConfig: unknown,
  thresholds: PaginationThresholds = DEFAULT_PAGINATION_THRESHOLDS
): string | null {
  // If pagination is disabled but we have more than recommended items
  if (paginationConfig === false && dataLength > thresholds.noPagination) {
    return `Warning: ${dataLength} items with no pagination. Consider using pagination for datasets > ${thresholds.noPagination} items for better UX.`;
  }
  
  // If pagination is enabled but we have very few items
  if (paginationConfig !== false && dataLength <= thresholds.noPagination) {
    return `Info: Only ${dataLength} items but pagination is enabled. Consider disabling pagination for cleaner UX with small datasets.`;
  }
  
  return null;
}

/**
 * Standard pagination configuration for client-side tables
 */
export const CLIENT_SIDE_PAGINATION_CONFIG = {
  pageSize: 10,
  showSizeSelector: true,
  showPageInfo: true,
  showNavigation: true,
  pageSizeOptions: [10, 25, 50, 100],
  enableFastNavigation: false,
  enableJumpToPage: false,
};

/**
 * Standard pagination configuration for server-side tables
 */
export const SERVER_SIDE_PAGINATION_CONFIG = {
  pageSize: 10,
  showSizeSelector: true,
  showPageInfo: true,
  showNavigation: true,
  enableFastNavigation: true,
  enableJumpToPage: true,
  pageSizeOptions: [10, 25, 50, 100, 200],
};

/**
 * Converts TanStack 0-based pageIndex to backend 1-based page number
 * 
 * @param pageIndex - TanStack 0-based page index
 * @returns Backend 1-based page number
 */
export function pageIndexToPage(pageIndex: number): number {
  return pageIndex + 1;
}

/**
 * Converts backend 1-based page number to TanStack 0-based pageIndex
 * 
 * @param page - Backend 1-based page number
 * @returns TanStack 0-based page index
 */
export function pageToPageIndex(page: number): number {
  return Math.max(0, page - 1);
}

/**
 * Creates a debounced function that delays invoking func until after wait milliseconds
 * have elapsed since the last time the debounced function was invoked.
 * 
 * @param func - The function to debounce
 * @param wait - The number of milliseconds to delay
 * @returns Debounced function
 */
export function debounce<T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), wait);
  };
} 